import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, doc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminMessagesView.css';
import { schoolMatches } from '../utils/schoolScope';

const toDate = (v) => (!v ? null : v.toDate ? v.toDate() : new Date(v));
const fmt = (v) => { const d = toDate(v); return d && !isNaN(d) ? d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : ''; };

function SuperAdminMessagesView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  const [studentSchoolById, setStudentSchoolById] = useState({});
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
    // Conversations have no schoolId — scope via the student's school (case-insensitive)
    getDocs(collection(db, 'students')).then(snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data().schoolId; });
      setStudentSchoolById(map);
    }).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('') = national view
  const effectiveSchoolId = saSchoolId || null;
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: effectiveSchoolId || adminData?.schoolId || null }), [adminData, effectiveSchoolId]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [msgCounts, setMsgCounts] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'teacherParentConversations'), where('conversationType', '==', 'admin'));
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (toDate(b.lastMessageAt) || 0) - (toDate(a.lastMessageAt) || 0));
      setConversations(list);
      setLoading(false);
    }, (err) => { console.error('Error loading admin conversations:', err); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!selected) { setMessages([]); return undefined; }
    return onSnapshot(collection(db, 'teacherParentConversations', selected.id, 'messages'), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (toDate(a.sentAt) || 0) - (toDate(b.sentAt) || 0));
      setMessages(list);
      setMsgCounts(list.length);
    });
  }, [selected]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const selectConversation = async (c) => {
    setSelected(c);
    if (c.unreadCount > 0) {
      try { await updateDoc(doc(db, 'teacherParentConversations', c.id), { unreadCount: 0 }); } catch (e) { console.error(e); }
    }
  };

  // Scope conversations by school via the student's schoolId (case-insensitive)
  const conversationSchoolId = (c) => c.schoolId || studentSchoolById[c.studentId] || '';
  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || sid || '';
  const scopedConversations = useMemo(() =>
    !effectiveSchoolId
      ? conversations
      : conversations.filter(c => schoolMatches(conversationSchoolId(c), effectiveSchoolId)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [conversations, effectiveSchoolId, studentSchoolById]);

  const stats = useMemo(() => {
    const unread = scopedConversations.filter(c => c.unreadCount > 0).length;
    return {
      total: scopedConversations.length,
      unread,
      unreadMsgs: scopedConversations.reduce((s, c) => s + (c.unreadCount || 0), 0),
      read: scopedConversations.length - unread,
      msgs: selected ? msgCounts : 0
    };
  }, [scopedConversations, msgCounts, selected]);

  const filtered = scopedConversations.filter(c =>
    !search || `${c.parentName} ${c.studentName} ${c.lastMessage}`.toLowerCase().includes(search.toLowerCase()));

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || !selected) return;
    const now = new Date().toISOString();
    try {
      await addDoc(collection(db, 'teacherParentConversations', selected.id, 'messages'), {
        conversationId: selected.id,
        senderId: adminData?.uid || '',
        senderName: adminData?.displayName || 'Admin',
        senderRole: adminData?.role === 'superadmin' ? 'superadmin' : 'admin',
        recipientId: selected.parentId,
        recipientName: selected.parentName,
        message: body,
        studentId: selected.studentId,
        studentName: selected.studentName,
        isRead: false,
        sentAt: now
      });
      await updateDoc(doc(db, 'teacherParentConversations', selected.id), {
        lastMessage: body, lastMessageAt: now, lastMessageSenderId: adminData?.uid || '', unreadCount: 0
      });
      setText('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div><p>Loading messages...</p></div>;

  const cards = [
    ['Conversations', stats.total, 'blue'], ['Unread', stats.unread, 'orange'], ['Unread Msgs', stats.unreadMsgs, 'red'],
    ['Read', stats.read, 'green'], ['Msgs (open chat)', stats.msgs, 'purple']
  ];

  return (
    <div className="admin-messages-view">
      <h2>Messages</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
      <div className="am-stats">
        {cards.map(([l, v, c]) => (
          <div key={l} className={`am-stat ${c}`}><div className="am-stat-value">{v}</div><div className="am-stat-label">{l}</div></div>
        ))}
      </div>
      <div className={`am-layout ${selected ? 'has-selection' : ''}`}>
        <div className="am-list">
          <input type="text" placeholder="Search parent, student..." value={search} onChange={(e) => setSearch(e.target.value)} />
          {filtered.length === 0 ? <p className="am-empty">No conversations</p> : filtered.map(c => (
            <div key={c.id} className={`am-item ${selected?.id === c.id ? 'active' : ''}`} onClick={() => selectConversation(c)}>
              <div className="am-item-top"><strong>{c.parentName}</strong>{c.unreadCount > 0 && <span className="am-badge">{c.unreadCount}</span>}</div>
              <small>Re: {c.studentName}</small>
              {schoolNameFor(conversationSchoolId(c)) && <small>🏫 {schoolNameFor(conversationSchoolId(c))}</small>}
              <p>{c.lastMessage || 'No messages yet'}</p>
              <small>{fmt(c.lastMessageAt)}</small>
            </div>
          ))}
        </div>
        <div className="am-chat">
          {!selected ? <div className="am-empty">Select a conversation</div> : (
            <>
              <div className="am-chat-header">
                <button className="am-back" onClick={() => setSelected(null)}>←</button>
                <div><strong>{selected.parentName}</strong><br /><small>Re: {selected.studentName}</small></div>
              </div>
              <div className="am-messages">
                {messages.map(m => {
                  const mine = m.senderId === adminData?.uid || m.senderRole === 'admin' || m.senderRole === 'superadmin';
                  return (
                    <div key={m.id} className={`am-msg ${mine ? 'mine' : 'theirs'}`}>
                      <div className="am-msg-body">{m.message}</div>
                      <small>{m.senderName} · {fmt(m.sentAt)}</small>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form className="am-send" onSubmit={send}>
                <input type="text" placeholder="Type a reply..." value={text} onChange={(e) => setText(e.target.value)} />
                <button type="submit" disabled={!text.trim()}>Send</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SuperAdminMessagesView;
